package org.dronda.app.web.ui.eventadmin

import kotlinx.datetime.TimeZone
import org.dronda.api.wishlist.EventLimits
import org.dronda.app.web.ui.components.ExclusionLabel
import org.dronda.app.web.ui.components.SelectOption
import org.dronda.app.web.ui.util.exclusionLabel
import org.dronda.app.web.ui.util.formatShort
import org.dronda.service.wishlist.core.domain.EventAdminDetails
import org.dronda.service.wishlist.core.domain.Exclusion
import org.dronda.service.wishlist.core.domain.Participant
import org.dronda.service.wishlist.core.domain.SantaSendKind
import kotlin.uuid.Uuid

data class PersonRow(val label: String, val status: String?, val isNew: Boolean)

data class ExclusionRow(val label: ExclusionLabel, val exclusion: Exclusion)

data class SendPanel(
    val sendsLabel: String,
    val summary: String,
    val options: List<SendOption>,
    val buttonLabel: String?,
)

data class SendOption(val kind: SantaSendKind, val title: String, val description: String)

internal fun personRows(details: EventAdminDetails, timeZone: TimeZone): List<PersonRow> {
    val santa = details.santa
    return details.participants.map { participant ->
        PersonRow(
            label = participant.displayName(),
            status = when {
                santa == null -> null
                participant.isAssigned -> buildString {
                    append("✓ assigned")
                    participant.lastEmailedAt?.let { append(" · emailed ${it.formatShort(timeZone)}") }
                }
                santa.isDrawn -> "● new — not assigned"
                else -> null
            },
            isNew = santa?.isDrawn == true && !participant.isAssigned,
        )
    }
}

internal fun Participant.displayName(): String = if (isAdmin) "${user.name} (you)" else user.name

internal fun choices(details: EventAdminDetails): List<SelectOption> =
    details.participants.map { SelectOption(value = it.user.id.toString(), label = it.user.name) }

internal fun exclusionRows(details: EventAdminDetails, exclusions: List<Exclusion>): List<ExclusionRow> {
    val names = details.participants.associate { it.user.id to it.user.name }
    return exclusions.map { exclusion ->
        ExclusionRow(exclusionLabel(names[exclusion.userA] ?: "?", names[exclusion.userB] ?: "?", exclusion.direction), exclusion)
    }
}

internal fun List<Exclusion>.containsPair(a: Uuid, b: Uuid): Boolean =
    any { (it.userA == a && it.userB == b) || (it.userA == b && it.userB == a) }

internal fun sendPanel(details: EventAdminDetails): SendPanel? {
    val santa = details.santa ?: return null
    val allowed = santa.allowedSendKinds
    val sendsLabel = "Sends used: ${santa.sendsUsed} / ${santa.maxSends}"
    val peopleCount = details.participants.size
    val newcomers = santa.unassignedCount

    val summary = when {
        SantaSendKind.INITIAL in allowed ->
            "Draw names for all $peopleCount people and email each person who they're buying for."
        allowed.isNotEmpty() -> buildList {
            if (newcomers > 0) add("$newcomers new ${if (newcomers == 1) "person" else "people"} since the last send.")
            if (santa.exclusionsChanged) add("Exclusions changed since the last send. Reshuffle to apply them.")
        }.joinToString(" ")
        santa.sendsUsed >= santa.maxSends ->
            "You've used all ${santa.maxSends} sends for this event."
        peopleCount < EventLimits.MIN_SANTA_PARTICIPANTS ->
            "Add at least ${EventLimits.MIN_SANTA_PARTICIPANTS} people to draw names."
        else ->
            "Everyone has been emailed. You can send again after someone new joins or the exclusions change."
    }

    val options = allowed.mapNotNull { kind ->
        when (kind) {
            SantaSendKind.INITIAL -> null
            SantaSendKind.SPLICE -> SendOption(
                kind = kind,
                title = "Add newcomers only",
                description = "Changes $newcomers other ${if (newcomers == 1) "person's assignment" else "people's assignments"} " +
                    "and emails ${newcomers * 2} people",
            )
            SantaSendKind.RESHUFFLE -> SendOption(
                kind = kind,
                title = "Full reshuffle",
                description = "New draw for everyone and emails all $peopleCount people",
            )
        }
    }

    val buttonLabel = when {
        SantaSendKind.INITIAL in allowed -> "Draw names & email everyone"
        allowed.isNotEmpty() -> "Send emails"
        else -> null
    }

    return SendPanel(sendsLabel, summary, options, buttonLabel)
}
